import { Stack, Typography, useTheme } from '@mui/joy';
import { upperFirst } from 'lodash';
import { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { SlidingPanel, Tabs } from 'core/components';
import { ColoredChip, LabeledValue, TextEllipsis, UpdatedAtSubtitle } from 'core/elements';
import { useGetPartBom } from 'partBoms/hooks';
import { IPartBom, IPartBomItem } from 'partBoms/types';
import { PartNumberHelper } from 'parts/helpers';
import { Direction, Level } from 'theme/enums';

import { PartBomDetailsTab } from './enums';
import { PartBomCards } from './PartBomCards';
import { Details, ItemList } from './tabs';
import { getTabStyle } from './utils';

interface IProps {
  isOpen?: boolean;
  onClose?: () => void;
  partBom: IPartBom;
  defaultTab?: PartBomDetailsTab;
  fullScreen?: boolean;
  onSaveItemList?: (items: IPartBomItem[]) => void;
  inline?: boolean;
  isEditable?: boolean;
}

export const PartBomDetails = ({
  isOpen,
  onClose,
  partBom,
  defaultTab = PartBomDetailsTab.PART_DETAILS,
  fullScreen = false,
  onSaveItemList,
  inline = false,
  isEditable = true,
}: IProps) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const { record: partBomDetails, isLoading } = useGetPartBom(
    {
      part: { id: partBom?.id },
    },
    { enabled: !!partBom?.id }
  );

  const tabs = useMemo(
    () => [
      {
        value: PartBomDetailsTab.PART_DETAILS,
        label: t('labelPartDetails'),
        content: <Details partBom={partBomDetails} isLoading={isLoading} isEditable={isEditable} />,
        style: getTabStyle(PartBomDetailsTab.PART_DETAILS, { fullScreen, inline }),
      },
      {
        value: PartBomDetailsTab.ITEM_LIST,
        label: t('labelPartBomItemList'),
        content: (
          <ItemList
            partBom={partBom}
            onClose={onClose}
            onSaveItemList={onSaveItemList}
            inline={inline}
            isPartBomEditable={isEditable}
          />
        ),
        style: getTabStyle(PartBomDetailsTab.ITEM_LIST, { fullScreen, inline }),
      },
    ],
    [isLoading, partBom, partBomDetails, t, fullScreen, onClose, onSaveItemList, inline, isEditable]
  );

  const partNumber = useMemo(
    () => partBomDetails?.number || partBom?.number,
    [partBomDetails?.number, partBom?.number]
  );

  const partBomType = useMemo(
    () => partBomDetails?.bomType || partBom?.bomType,
    [partBomDetails?.bomType, partBom?.bomType]
  );

  const updatedAt = useMemo(
    () => partBomDetails?.updatedAt || partBom?.updatedAt,
    [partBomDetails?.updatedAt, partBom?.updatedAt]
  );

  const descriptionLong = useMemo(
    () => partBomDetails?.descriptionLong || partBom?.descriptionLong,
    [partBomDetails?.descriptionLong, partBom?.descriptionLong]
  );

  const title = useMemo(
    () => (
      <Stack direction={Direction.ROW} sx={{ gap: 1, paddingRight: '32px', alignItems: 'center' }}>
        <Typography level={Level.H3} sx={{ overflowWrap: 'anywhere', fontWeight: 500 }}>
          <Trans
            i18nKey="titlePartBomDetails"
            values={{
              partBomNumber: PartNumberHelper.toLabel(partNumber),
            }}
          />
        </Typography>

        <ColoredChip value={upperFirst(partBomType)} sx={{ height: 'fit-content' }} />
      </Stack>
    ),
    [partBomType, partNumber]
  );

  const subtitle = useMemo(
    () => (
      <>
        <UpdatedAtSubtitle updatedAt={updatedAt} />

        <LabeledValue
          label={t('labelPartBomDescription')}
          value={
            <TextEllipsis
              content={descriptionLong}
              sx={{ color: theme.palette.neutral[800], fontWeight: 600 }}
            />
          }
          options={{
            labelLevel: Level.BODY_SM,
            wrapperProps: { direction: Direction.ROW, gap: '4px' },
          }}
        />
      </>
    ),
    [updatedAt, t, descriptionLong, theme.palette.neutral]
  );

  const content = useMemo(() => {
    return (
      <Tabs
        tabs={tabs}
        ariaLabel="partBomDetails"
        defaultTab={defaultTab}
        sx={{ width: inline ? '100%' : 'fit-content' }}
      />
    );
  }, [tabs, defaultTab, inline]);

  if (inline) {
    return content;
  }

  return (
    <SlidingPanel
      isOpen={isOpen}
      onClosePanel={onClose}
      title={title}
      subtitle={subtitle}
      extra={<PartBomCards partBom={partBomDetails} />}
      fullScreen={fullScreen}
      autoWidth
      inheritContentWidth
    >
      {content}
    </SlidingPanel>
  );
};
